import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer } from "lucide-react";

export default function MesseReport() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, fmtMoney } = useI18n();
  const [rep, setRep] = useState(null);

  useEffect(() => { api.get(`/trade-fairs/${id}/report`).then((r) => setRep(r.data)).catch(() => {}); }, [id]);
  if (!rep) return <div className="text-muted-foreground p-8">{t("common.loading")}</div>;
  const fr = rep.fair;
  const tot = rep.totals;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 print:hidden">
        <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="messe-report-back"><ArrowLeft className="h-4 w-4" /> {t("common.back")}</button>
        <Button onClick={() => window.print()} data-testid="messe-report-print"><Printer className="h-4 w-4 mr-1.5" /> {t("common.printPdf")}</Button>
      </div>

      <div className="mx-auto max-w-3xl rounded-md border border-border bg-white text-black p-10 shadow-sm" data-testid="messe-report-doc">
        <div className="flex justify-between items-start">
          <div>
            <div className="font-heading text-2xl font-bold">{t("mer.title")}</div>
            <div className="text-xs text-neutral-600">Gluty-Free Club · Consberry GmbH</div>
          </div>
          <div className="text-right text-xs text-neutral-600">
            <div className="font-heading text-lg font-semibold text-black">{fr.name}</div>
            <div>{fr.location || "—"}</div>
            <div>{fr.start_date || "—"}{fr.end_date ? ` – ${fr.end_date}` : ""}</div>
            <div>{t("common.status")}: {fr.status === "abgeschlossen" ? t("mer.statusClosed") : t("mer.statusOpen")}</div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-4 gap-3 text-center">
          <div className="rounded border border-neutral-200 p-2"><div className="text-[10px] uppercase text-neutral-500">{t("common.revenue")}</div><div className="font-mono font-bold">{fmtMoney(tot.revenue)}</div></div>
          <div className="rounded border border-neutral-200 p-2"><div className="text-[10px] uppercase text-neutral-500">{t("mer.units")}</div><div className="font-mono font-bold">{tot.units}</div></div>
          <div className="rounded border border-neutral-200 p-2"><div className="text-[10px] uppercase text-neutral-500">{t("common.cogs")}</div><div className="font-mono font-bold">{fmtMoney(tot.cogs)}</div></div>
          <div className="rounded border border-neutral-200 p-2"><div className="text-[10px] uppercase text-neutral-500">{t("cntr.contribution")}</div><div className="font-mono font-bold">{fmtMoney(tot.contribution_margin)}</div></div>
        </div>

        <h3 className="mt-8 mb-2 font-heading text-lg font-semibold">{t("mer.salesPerItem")}</h3>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-neutral-300 text-left"><th className="py-1">SKU</th><th>{t("cnt.item")}</th><th className="text-right pl-3 align-bottom">{t("common.quantity")}</th><th className="text-right pl-3 align-bottom">{t("me.pricePer")}</th><th className="text-right pl-3 align-bottom">{t("common.revenue")}</th><th className="text-right pl-3 align-bottom">{t("mer.cmShort")}</th></tr></thead>
          <tbody data-testid="messe-report-lines">
            {rep.lines.map((l, i) => (
              <tr key={l.sku || i} className="border-b border-neutral-100">
                <td className="py-1 font-mono">{l.sku}</td><td>{l.name}</td>
                <td className="text-right font-mono whitespace-nowrap">{l.quantity}</td><td className="text-right font-mono whitespace-nowrap">{fmtMoney(l.unit_price)}</td>
                <td className="text-right font-mono whitespace-nowrap">{fmtMoney(l.revenue)}</td><td className="text-right font-mono whitespace-nowrap">{fmtMoney(l.contribution_margin)}</td>
              </tr>
            ))}
            {!rep.lines.length && <tr><td className="py-2 text-neutral-500" colSpan={6}>{t("me.noSales")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
